<template>
    <div>
        <TextField
            v-model="modelValue.holder"
            name="bank_account_holder"
            label="account_holder"
            help="help_bank_account_holder"
            :required="true"
        />
        <TextField
            v-model="modelValue.iban"
            :mask="ibanMask"
            name="bank_account_iban"
            label="iban"
            help="help_bank_account_iban"
            :required="true"
            :error-text="ibanErrorText"
        />
        <TextField
            v-model="modelValue.bic"
            name="bank_account_bic"
            label="bic"
            help="help_bank_account_bic"
            :error-text="bicErrorText"
        />
    </div>
</template>

<script setup lang="ts">
import { useIbanValidation, useMask } from '@composables/common'
import { TextField } from '@src/components'
import { BankAccount } from '@src/types'
import { Ref, computed, toRefs, watch } from 'vue'

const props = defineProps<{
    modelValue: Ref<BankAccount>
}>()

const { modelValue } = toRefs(props)

const emit = defineEmits(['update:modelValue', 'update:valid'])

const { ibanMask } = useMask()
const { isValidIban, isValidBic } = useIbanValidation()

const ibanErrorText = computed(() => {
    if (!modelValue.value.iban) {
        return null
    }
    return isValidIban(modelValue.value.iban) ? null : 'error_invalid_iban'
})

const bicErrorText = computed(() => {
    if (!modelValue.value.bic) {
        return null
    }
    return isValidBic(modelValue.value.bic) ? null : 'error_invalid_bic'
})

const isValid = computed(() => {
    return (
        !!modelValue.value.holder &&
        !!modelValue.value.iban &&
        isValidIban(modelValue.value.iban) &&
        (!modelValue.value.bic || isValidBic(modelValue.value.bic))
    )
})

watch(
    modelValue,
    (value) => {
        emit('update:modelValue', value)
    },
    { deep: true }
)

watch(
    isValid,
    (value) => {
        emit('update:valid', value)
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped></style>
